import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import ReportEngineService from "service/ReportEngineService";
import { hasReportEngineAccess } from "helpers/hasPrivilege";
import CustomDropdown from "component/CustomDropdown";
import { PageSizeOption } from "domain/constant/PageSizeOption";
import { useNavPillsIndicator } from "hooks/useNavPillsIndicator";

const ReportEngine: React.FC = () => {
  const getStorageKey = (reportId: string) => `reportEngineParams_${reportId}`;
  const { profile } = useSelector((state: any) => state.auth);
  const [data, setData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [parameterValues, setParameterValues] = useState<Record<string, string>>({});
  const [executing, setExecuting] = useState(false);

  const [tabFilter, setTabFilter] = useState<"all" | "favorite">("all");

  const canCreate = hasReportEngineAccess(profile, "create");
  const canUpdate = hasReportEngineAccess(profile, "update");
  const canDelete = hasReportEngineAccess(profile, "delete");

  const reportEngineService = ReportEngineService();

  const { navRef: tabFilterNavRef, indicatorStyle: tabFilterIndicatorStyle } = useNavPillsIndicator(tabFilter);

  const fetchData = async () => {
    const token = profile?.data?.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setLoading(true);
    try {
      const isFavorite = tabFilter === "favorite";

      const res = await reportEngineService.reportEngineList(
        token,
        currentPage,
        pageSize,
        searchTerm || null,
        isFavorite
      );

      const allData = res.data ?? [];

      setData(allData);
      setTotalPages(res.pagination?.totalPage ?? 1);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error("Error fetching report engine list:", err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data report engine';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, tabFilter]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchData();
  };

  const handleDelete = async (id: string, name: string) => {
    const token = profile?.data?.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    const confirmDelete = window.confirm(`Hapus report engine "${name}"?`);
    if (!confirmDelete) return;

    try {
      await reportEngineService.reportEngineDelete(token, id);
      window.alert(`"${name}" berhasil dihapus.`);
      localStorage.removeItem(getStorageKey(id));
      fetchData();
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Gagal menghapus data.';
      window.alert(`Error: ${errorMessage}`);
    }
  };

  const handleExecuteClick = async (item: any) => {
    const token = profile?.data?.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      const res = await reportEngineService.reportEngineDetail(token, item.id);
      const detail = res.data;

      if (!detail) {
        window.alert("Detail report tidak ditemukan.");
        return;
      }

      setSelectedReport(detail);

      const savedParams = localStorage.getItem(getStorageKey(detail.id));
      let initialParams: Record<string, string> = {};

      if (Array.isArray(detail?.parameter)) {
        detail.parameter.forEach((param: string) => {
          initialParams[param] = "";
        });
      }

      if (savedParams) {
        try {
          const parsed = JSON.parse(savedParams);
          initialParams = { ...initialParams, ...parsed };
        } catch {
          console.warn("Gagal parse localStorage parameter");
        }
      }

      setParameterValues(initialParams);
      setShowModal(true);
    } catch (err: any) {
      console.error("Error fetching detail:", err);
      const errorMessage = err.response?.data?.message || err.message || "Gagal memuat detail report engine.";
      window.alert(`Error: ${errorMessage}`);
    }
  };

  const handleParameterChange = (paramName: string, value: string) => {
    setParameterValues((prev) => {
      const updated = { ...prev, [paramName]: value };

      if (selectedReport?.id) {
        localStorage.setItem(getStorageKey(selectedReport.id), JSON.stringify(updated));
      }

      return updated;
    });
  };

  const handleRunReport = async () => {
    if (!selectedReport) {
      window.alert('Report tidak ditemukan');
      return;
    }

    const token = profile?.data?.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setExecuting(true);
    try {
      const filteredParams: Record<string, any> = {};
      Object.keys(parameterValues).forEach((key) => {
        if (parameterValues[key].trim() !== "") {
          const value = parameterValues[key];
          if (!isNaN(Number(value)) && value.trim() !== "") {
            filteredParams[key] = Number(value);
          } else if (value.toLowerCase() === "true" || value.toLowerCase() === "false") {
            filteredParams[key] = value.toLowerCase() === "true";
          } else {
            filteredParams[key] = value;
          }
        }
      });

      await reportEngineService.reportEngineExecute(token, {
        id: selectedReport.id,
        params: filteredParams,
      });

      const extension = selectedReport.type.toLowerCase() === "csv" ? "csv" : "xlsx";

      const res = await reportEngineService.reportEngineDownload(
        token,
        selectedReport.code,
        filteredParams,
        extension
      );

      window.alert(res?.message || `File report "${selectedReport.name}" sedang diproses dan akan dikirimkan ke email Anda.`);

      setShowModal(false);
      setParameterValues({});
    } catch (err: any) {
      console.error("Error executing report:", err);
      const errorMessage = err.response?.data?.message || err.message || "Gagal menjalankan report engine.";
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setExecuting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedReport(null);
  };

  const handleToggleFavorite = async (item: any) => {
    const token = profile?.data?.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      const newFavoriteState = !(item.is_favorite || item.isFavorite);

      const res = await reportEngineService.reportEngineToggleFavorite(
        token,
        item.id,
        newFavoriteState
      );

      if (res.status === "T") {
        fetchData();
      }
    } catch (err: any) {
      console.error("Failed to toggle favorite:", err);
      const errorMessage = err.response?.data?.message || err.message || "Gagal mengubah status favorite.";
      window.alert(`Error: ${errorMessage}`);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-center mb-3">
            <h5 style={{ flex: 1 }}>Report Engine</h5>
            <p className="mb-0">TransMore Report Engine</p>
          </div>

          <div className="p-4 bg-white rounded shadow-sm">
            <div className="d-flex align-items-center mb-3 flex-nowrap gap-2 overflow-auto text-nowrap">
              <form className="d-flex me-auto" onSubmit={handleSearchClick}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: 120 }}
                />
                <button type="submit" className="btn btn-primary px-4">
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>

              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div ref={tabFilterNavRef} className="nav-pills-animated">
                  <div
                    className="nav-pill-indicator"
                    style={tabFilterIndicatorStyle}
                  />
                  <button
                    type="button"
                    className={`nav-link btn ${tabFilter === "all" ? "active" : ''}`}
                    onClick={() => {
                      setTabFilter("all");
                      setCurrentPage(1);
                    }}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`nav-link btn ${tabFilter === "favorite" ? "active" : ''}`}
                    onClick={() => {
                      setTabFilter("favorite");
                      setCurrentPage(1);
                    }}
                  >
                    Favorites Only
                  </button>
                </div>

                {canCreate && (
                  <Link to="/report-engine/0" className="text-decoration-none">
                    <button className="btn btn-primary">
                      <span className="me-2">Add</span>
                      <i className="fa-solid fa-plus"></i>
                    </button>
                  </Link>
                )}
              </div>
            </div>

            <table className="table table-condensed table-hover">
              <thead className="table-primary">
                <tr>
                  <th style={{ width: "50px" }}></th>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Created At</th>
                  <th>Created By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {!loading ? (
                  data.length > 0 ? (
                    data.map((item) => (
                      <tr key={item.id}>
                        <td className="text-center">
                          <i
                            className={`fa-star ${item.is_favorite || item.isFavorite
                              ? "fa-solid text-warning"
                              : "fa-regular text-secondary"
                              }`}
                            style={{ cursor: "pointer", fontSize: "18px" }}
                            onClick={() => handleToggleFavorite(item)}
                            title={
                              item.is_favorite || item.isFavorite
                                ? "Remove from favorites"
                                : "Add to favorites"
                            }
                          ></i>
                        </td>

                        <td>{item.code}</td>
                        <td>{item.name}</td>
                        <td>
                          <span>
                            {item.type}
                          </span>
                        </td>
                        <td>{item.createdAt}</td>
                        <td>{item.createdBy}</td>
                        <td>
                          <div className="d-flex">
                            <Link to={`/report-engine-information/${item.id}`}>
                              <button
                                className="btn btn-outline-primary btn-small-compact border-0"
                                title="View Report"
                              >
                                <i className="fa-solid fa-eye"></i>
                              </button>
                            </Link>
                            <button
                              className="btn btn-outline-success btn-small-compact border-0"
                              onClick={() => handleExecuteClick(item)}
                              title="Execute"
                            >
                              <i className="fa-solid fa-play-circle"></i>
                            </button>
                            {
                              canUpdate && (
                                <Link to={`/report-engine/${item.id}`}>
                                  <button
                                    className="btn btn-outline-warning btn-small-compact border-0"
                                    title="Edit Report"
                                  >
                                    <i className="fa-solid fa-edit"></i>
                                  </button>
                                </Link>
                              )
                            }
                            {
                              canDelete && (
                                <button
                                  className="btn btn-outline-danger btn-small-compact border-0 ms-2"
                                  onClick={() => handleDelete(item.id, item.name)}
                                  title="Delete Report"
                                >
                                  <i className="fa-solid fa-trash"></i>
                                </button>
                              )
                            }
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">
                        {tabFilter === "favorite"
                          ? "Tidak ada report engine yang difavoritkan"
                          : "Tidak ada data"
                        }
                      </td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="d-flex justify-content-end align-items-center mt-3">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {`${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
                </span>

                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>
                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && selectedReport && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)" }}
          onClick={handleCloseModal}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content p-4">
              <h5 className="fw-bold mb-4">
                Parameter - {selectedReport.name}
              </h5>

              <div className="mb-3">
                {selectedReport.parameter && selectedReport.parameter.length === 0 ? (
                  <p className="text-muted fst-italic">
                    Report ini tidak memiliki parameter.
                  </p>
                ) : (
                  selectedReport.parameter?.map((param: string, index: number) => (
                    <div key={index} className="mb-3">
                      <label className="form-label fw-semibold">{param}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={parameterValues[param] || ""}
                        onChange={(e) => handleParameterChange(param, e.target.value)}
                        placeholder={`Masukkan nilai untuk ${param}`}
                      />
                    </div>
                  ))
                )}
              </div>

              <div className="d-flex justify-content-end gap-2">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleCloseModal}
                  disabled={executing}
                >
                  Batal
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleRunReport}
                  disabled={executing}
                >
                  {executing ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Executing...
                    </>
                  ) : (
                    "Execute"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportEngine;